import React from "react";

import { AlertCircle, Check, Copy as CopyIcon } from "lucide-react";

import { cn } from "lib/utils";

import { useClipboard } from "hooks/useClipboard";

import { CopyText } from "js/components/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "js/components/ui/tooltip";

const PLACEHOLDER = "Aún no proporcionado";

const CopyClipboard: React.FC<CopyText> = ({ title, text }) => {
  const sanitizedValue =
    typeof text === "string" && text.trim().length > 0 ? text.trim() : "";
  const hasValue = sanitizedValue.length > 0;
  const displayValue = hasValue ? text : PLACEHOLDER;

  const { isCopied, copy, error } = useClipboard({
    timeout: 1200,
  });

  const handleCopy = async () => {
    if (!hasValue) {
      return;
    }

    await copy(sanitizedValue);
  };

  const tooltipLabel = error
    ? "No se pudo copiar. Intenta nuevamente."
    : isCopied
      ? "Copiado"
      : hasValue
        ? "Copiar al portapapeles"
        : "Sin datos para copiar";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleCopy}
          aria-disabled={!hasValue}
          className={cn(
            "group flex w-full items-center justify-between rounded-md border px-3 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            isCopied
              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
              : "border-slate-200 bg-white text-slate-900 hover:border-slate-300",
            !hasValue &&
              !isCopied &&
              "cursor-default text-slate-500 hover:border-slate-200",
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 shrink-0">
              {title}
            </span>
            <span
              className={cn(
                "text-sm leading-5 text-slate-900 break-words",
                hasValue ? "sm:truncate" : "italic text-slate-500",
              )}
              title={hasValue ? sanitizedValue : undefined}
            >
              {displayValue}
            </span>
          </span>
          <span
            aria-hidden="true"
            className={cn(
              "ml-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border transition",
              isCopied
                ? "border-emerald-300 bg-emerald-100 text-emerald-700"
                : "border-slate-200 bg-slate-50 text-slate-500 group-hover:border-slate-300 group-hover:bg-white",
              !hasValue &&
                !isCopied &&
                "border-transparent bg-slate-100 text-slate-300 group-hover:border-transparent group-hover:bg-slate-100",
            )}
          >
            {error ? (
              <AlertCircle className="size-4" />
            ) : isCopied ? (
              <Check className="size-4" />
            ) : (
              <CopyIcon className="size-4" />
            )}
          </span>
          <span className="sr-only" aria-live="polite">
            {error
              ? `No se pudo copiar ${title}`
              : isCopied
                ? `${title} copiado`
                : ""}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{tooltipLabel}</TooltipContent>
    </Tooltip>
  );
};

export default CopyClipboard;
